import { useEffect, useState } from 'react';
import { Microphone, SpeakerSimpleHigh, SpeakerX, MusicNote, Metronome, Timer } from '@phosphor-icons/react';
import { CUES } from '@/constants/testIds';
import { setMode as setStoreMode } from '@/lib/audioCue';

const DEFAULTS = {
    tempo_eccentric: 3,
    tempo_pause_bottom: 1,
    tempo_concentric: 1,
    tempo_pause_top: 0,
    rest_compound_seconds: 120,
    rest_isolation_seconds: 60,
    enable_rep_pacing: true,
    voice_command_enabled: false,
    cue_mode: 'voice',
};

const MODE_OPTIONS = [
    { id: 'off', label: 'Off', testId: CUES.cueModeOff, Icon: SpeakerX },
    { id: 'beeps', label: 'Beeps', testId: CUES.cueModeBeeps, Icon: MusicNote },
    { id: 'voice', label: 'Voice', testId: CUES.cueModeVoice, Icon: SpeakerSimpleHigh },
];

export function WorkoutCuesForm({ initial, onSave, saving }) {
    const [form, setForm] = useState(() => ({ ...DEFAULTS, ...(initial || {}) }));
    const [savedAt, setSavedAt] = useState(null);

    useEffect(() => {
        if (!initial) return;
        setForm({ ...DEFAULTS, ...initial });
    }, [initial]);

    const tempoString = `${form.tempo_eccentric}-${form.tempo_pause_bottom}-${form.tempo_concentric}-${form.tempo_pause_top}`;
    const repSeconds =
        Number(form.tempo_eccentric) +
        Number(form.tempo_pause_bottom) +
        Number(form.tempo_concentric) +
        Number(form.tempo_pause_top);

    function setField(k, v) {
        setForm((f) => ({ ...f, [k]: v }));
    }

    async function handleSubmit(e) {
        e.preventDefault();
        // Mirror the cue_mode into the in-session pub/sub store so the
        // floating RestTimer reflects the new choice immediately.
        setStoreMode(form.cue_mode);
        await onSave({ ...form });
        setSavedAt(Date.now());
    }

    return (
        <section
            data-testid={CUES.section}
            className="mt-8 glass-card rounded-3xl p-6 sm:p-8"
        >
            <header className="mb-6">
                <p className="uppercase-mono text-muted-foreground">
                    Workout cues
                </p>
                <h2 className="font-display font-extrabold tracking-tight text-2xl sm:text-3xl">
                    How GymFlow paces you
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                    Tempo, rest, and voice/beep cues — applied every workout.
                </p>
            </header>

            <form onSubmit={handleSubmit} className="grid gap-6">
                {/* Tempo */}
                <div>
                    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                        <p className="uppercase-mono text-muted-foreground inline-flex items-center gap-1.5">
                            <Metronome size={13} weight="bold" />
                            Tempo (sec per phase)
                        </p>
                        <p className="font-mono text-sm text-muted-foreground tabular-nums">
                            {tempoString} ·{' '}
                            <span className="text-foreground font-bold">
                                {repSeconds}s
                            </span>
                            /rep
                        </p>
                    </div>
                    <div className="grid grid-cols-4 gap-2 sm:gap-3">
                        <TempoCell
                            label="Down"
                            testId={CUES.tempoEcc}
                            value={form.tempo_eccentric}
                            onChange={(v) => setField('tempo_eccentric', v)}
                        />
                        <TempoCell
                            label="Pause"
                            testId={CUES.tempoBot}
                            value={form.tempo_pause_bottom}
                            onChange={(v) => setField('tempo_pause_bottom', v)}
                        />
                        <TempoCell
                            label="Up"
                            testId={CUES.tempoCon}
                            value={form.tempo_concentric}
                            onChange={(v) => setField('tempo_concentric', v)}
                        />
                        <TempoCell
                            label="Hold"
                            testId={CUES.tempoTop}
                            value={form.tempo_pause_top}
                            onChange={(v) => setField('tempo_pause_top', v)}
                        />
                    </div>
                </div>

                {/* Rest by category */}
                <div>
                    <p className="uppercase-mono text-muted-foreground inline-flex items-center gap-1.5 mb-3">
                        <Timer size={13} weight="bold" />
                        Rest between sets
                    </p>
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                        <RestCell
                            label="Compound (squat / bench / row)"
                            testId={CUES.restCompound}
                            value={form.rest_compound_seconds}
                            onChange={(v) => setField('rest_compound_seconds', v)}
                        />
                        <RestCell
                            label="Isolation (curl / raise / extension)"
                            testId={CUES.restIsolation}
                            value={form.rest_isolation_seconds}
                            onChange={(v) => setField('rest_isolation_seconds', v)}
                        />
                    </div>
                </div>

                {/* Cue mode */}
                <div>
                    <p className="uppercase-mono text-muted-foreground mb-3">
                        Cue mode
                    </p>
                    <div className="flex items-center gap-2 p-1.5 rounded-2xl border border-foreground/15 bg-foreground/5">
                        {MODE_OPTIONS.map((m) => {
                            const active = form.cue_mode === m.id;
                            return (
                                <button
                                    key={m.id}
                                    type="button"
                                    data-testid={m.testId}
                                    aria-pressed={active}
                                    onClick={() => setField('cue_mode', m.id)}
                                    className={`flex-1 inline-flex items-center justify-center gap-2 min-h-11 px-3 rounded-xl uppercase-mono transition-colors ${
                                        active
                                            ? 'bg-foreground text-background'
                                            : 'text-muted-foreground hover:text-foreground'
                                    }`}
                                >
                                    <m.Icon size={13} weight="bold" />
                                    {m.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Toggles */}
                <div className="grid sm:grid-cols-2 gap-3">
                    <ToggleRow
                        testId={CUES.repPacingToggle}
                        label="Rep pacing"
                        description="Counts you through each rep at the tempo above."
                        checked={!!form.enable_rep_pacing}
                        onChange={(v) => setField('enable_rep_pacing', v)}
                    />
                    <ToggleRow
                        testId={CUES.voiceCmdToggle}
                        label="Voice commands"
                        Icon={Microphone}
                        description="Say “start”, “next”, or “stop” during a workout. Requires mic access."
                        checked={!!form.voice_command_enabled}
                        onChange={(v) => setField('voice_command_enabled', v)}
                    />
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                    <button
                        type="submit"
                        data-testid={CUES.save}
                        disabled={saving}
                        className="h-12 px-6 rounded-2xl bg-primary text-primary-foreground font-bold tracking-tight shadow-neon hover:-translate-y-[2px] active:scale-[0.99] transition-all disabled:opacity-60"
                    >
                        {saving ? 'Saving…' : 'Save cues'}
                    </button>
                    {savedAt && !saving && (
                        <span className="uppercase-mono text-[#00FF9D]">
                            Saved · just now
                        </span>
                    )}
                </div>
            </form>
        </section>
    );
}

function TempoCell({ label, value, onChange, testId }) {
    return (
        <label className="block">
            <span className="uppercase-mono text-muted-foreground block mb-1.5">
                {label}
            </span>
            <input
                type="number"
                min={0}
                max={10}
                step={1}
                inputMode="numeric"
                value={value}
                onChange={(e) =>
                    onChange(Math.max(0, Math.min(10, parseInt(e.target.value || '0', 10))))
                }
                data-testid={testId}
                className="w-full h-12 rounded-2xl border border-foreground/15 bg-foreground/5 px-3 outline-none focus:border-foreground/40 transition-colors font-mono text-xl font-extrabold tabular-nums text-center"
            />
        </label>
    );
}

function RestCell({ label, value, onChange, testId }) {
    return (
        <label className="block">
            <span className="uppercase-mono text-muted-foreground block mb-1.5">
                {label}
            </span>
            <div className="flex items-center h-12 rounded-2xl border border-foreground/15 bg-foreground/5 px-3 focus-within:border-foreground/40 transition-colors">
                <input
                    type="number"
                    min={10}
                    max={600}
                    step={5}
                    inputMode="numeric"
                    value={value}
                    onChange={(e) =>
                        onChange(
                            Math.max(
                                10,
                                Math.min(600, parseInt(e.target.value || '60', 10))
                            )
                        )
                    }
                    data-testid={testId}
                    className="flex-1 min-w-0 bg-transparent outline-none font-mono text-xl font-extrabold tabular-nums"
                />
                <span className="uppercase-mono text-muted-foreground ml-1">
                    sec
                </span>
            </div>
        </label>
    );
}

function ToggleRow({ label, description, checked, onChange, testId, Icon }) {
    return (
        <label
            className="rounded-2xl border border-foreground/10 bg-foreground/[0.02] px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-foreground/5 transition-colors"
            data-testid={`${testId}-row`}
        >
            <div className="min-w-0 flex-1">
                <p className="font-semibold tracking-tight inline-flex items-center gap-1.5">
                    {Icon && <Icon size={14} weight="bold" />}
                    {label}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                    {description}
                </p>
            </div>
            <button
                type="button"
                role="switch"
                data-testid={testId}
                aria-checked={checked}
                onClick={() => onChange(!checked)}
                className={`relative shrink-0 w-12 h-7 rounded-full transition-colors ${
                    checked ? 'bg-[#00FF9D]' : 'bg-foreground/15'
                }`}
            >
                <span
                    className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-background transition-transform shadow ${
                        checked ? 'translate-x-5' : 'translate-x-0'
                    }`}
                />
            </button>
        </label>
    );
}
